import { useCallback, useEffect, useId, useState } from "react";
import { useDocumentTitle } from "../a11y";
import { api, ApiError } from "../api";
import { contactEmailStatus, distribution, FEEDBACK_QUESTIONS, feedbackText, humaniseId, saveTextFile, signedUpOn } from "../results";
import type { AdminSummary, Contact } from "../types";

const TOKEN_KEY = "lawsim.adminToken";

function readToken(): string {
  try { return sessionStorage.getItem(TOKEN_KEY) ?? ""; } catch { return ""; }
}
function writeToken(token: string | null) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable; the token lasts only while this page is open
  }
}

/** Simple horizontal bars for a count distribution; the count and share are always written out. */
function Bars({ title, counts }: { title: string; counts: Record<string, number> }) {
  const rows = distribution(counts);
  return (
    <div className="dist">
      <h3>{title}</h3>
      {rows.length === 0 ? <p className="muted">None yet.</p> : (
        <ul className="dist__list">
          {rows.map((r) => (
            <li key={r.key} className="dist__row">
              <span className="dist__label">{r.key}</span>
              <span className="dist__bar" aria-hidden="true"><span style={{ width: `${r.share * 100}%` }} /></span>
              <span className="dist__count num">{r.count} <span className="muted">({Math.round(r.share * 100)}%)</span></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Researcher results page (#/results). Not linked from the game; needs the admin token. */
export function Results() {
  const [token, setToken] = useState(readToken);
  const [draft, setDraft] = useState("");
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const ids = useId();
  useDocumentTitle(token ? "Playtest results" : "Researcher access");

  const load = useCallback(async (t: string) => {
    setLoading(true);
    setError(null);
    try {
      const [s, c] = await Promise.all([api.adminSummary(t), api.adminContacts(t)]);
      setSummary(s);
      setContacts(c.contacts);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        writeToken(null);
        setToken("");
        setSummary(null);
        setError("That token was not accepted.");
      } else {
        setError(e instanceof Error ? e.message : "Could not load the results.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) void load(token); }, [token, load]);
  useEffect(() => {
    api.scenarios().then((r) => setTitles(Object.fromEntries(r.scenarios.map((s) => [s.id, s.title])))).catch(() => undefined);
  }, []);

  const download = async () => {
    setExporting(true);
    setError(null);
    try {
      const data = await api.adminExport(token);
      saveTextFile(JSON.stringify(data, null, 2), `lawsim-export-${new Date().toISOString().slice(0, 10)}.json`, "application/json");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not download the export.");
    } finally {
      setExporting(false);
    }
  };

  const downloadContacts = async () => {
    setCsvBusy(true);
    setError(null);
    try {
      // A byte-order mark so spreadsheet programs read it as UTF-8 (names, en dashes).
      saveTextFile(`\uFEFF${await api.adminContactsCsv(token)}`, `lawsim-contacts-${new Date().toISOString().slice(0, 10)}.csv`, "text/csv;charset=utf-8");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not download the contacts.");
    } finally {
      setCsvBusy(false);
    }
  };

  const signOut = () => { writeToken(null); setToken(""); setSummary(null); setContacts(null); };

  if (!token) {
    return (
      <main className="entry">
        <form className="entry__card form" onSubmit={(e) => {
          e.preventDefault();
          const t = draft.trim();
          if (!t) return;
          writeToken(t);
          setDraft("");
          setToken(t);
        }}>
          <p className="entry__kicker">Playtest results</p>
          <h1 className="entry__title">Researcher access</h1>
          <label htmlFor={`${ids}-token`}>Admin token</label>
          <input id={`${ids}-token`} type="password" autoComplete="off" value={draft} onChange={(e) => setDraft(e.target.value)} required
            aria-describedby={`${ids}-note${error ? ` ${ids}-error` : ""}`} aria-invalid={error ? true : undefined} />
          <p className="muted" id={`${ids}-note`}>Kept for this browser tab only.</p>
          <div role="alert">{error && <p className="form__error" id={`${ids}-error`}>{error}</p>}</div>
          <button type="submit" className="btn btn--primary">Show results</button>
        </form>
      </main>
    );
  }

  return (
    <main className="entry entry--wide results">
      <div className="entry__bar">
        <span>Playtest results</span>
        <span className="row-actions" style={{ marginTop: 0 }}>
          <button type="button" className="btn" disabled={loading} onClick={() => void load(token)}>Refresh</button>
          <button type="button" className="btn btn--quiet" onClick={signOut}>Forget token</button>
        </span>
      </div>
      <h1 className="entry__title">Results by scenario</h1>
      {error && <p className="form__error" role="alert">{error}</p>}
      <div className="row-actions">
        <button type="button" className="btn btn--primary" disabled={exporting} onClick={() => void download()}>
          {exporting ? "Preparing…" : "Download full export (JSON)"}
        </button>
      </div>
      <p className="visually-hidden" role="status">{loading ? "Loading…" : summary ? "Results loaded." : ""}</p>
      {!summary ? <p aria-hidden="true">{loading ? "Loading…" : ""}</p> : (
        <>
          {summary.scenarios.length === 0 && <p className="muted">No runs yet.</p>}
          <ul className="results__scenarios">
            {summary.scenarios.map((s) => (
              <li key={`${s.scenarioId}:${s.mode ?? "full"}`} className="scenario-card">
                <h2>
                  {titles[s.scenarioId] ?? humaniseId(s.scenarioId)}
                  {s.mode === "quick" && <>{" "}<span className="mode-tag">Quick play</span></>}
                </h2>
                <p className="muted small">{s.scenarioId} · {s.mode === "quick" ? "quick play" : "full play"}</p>
                {s.mode === "quick" && (
                  <p className="quick-note">
                    Quick-play runs only. Routine decisions were handled the usual way, so these figures are kept apart from full play and
                    say less about how people lead.
                  </p>
                )}
                <dl className="results__stats">
                  <div><dt>Runs</dt><dd className="num">{s.runs}</dd></div>
                  <div><dt>Ended</dt><dd className="num">{s.ended}</dd></div>
                  <div><dt>Scoring revealed</dt><dd className="num">{s.scoringRevealed}</dd></div>
                  <div><dt>Mean final score</dt><dd className="num">{s.meanFinal ?? "–"}</dd></div>
                </dl>
                <Bars title="Endings" counts={Object.fromEntries(Object.entries(s.endings).map(([id, n]) => [s.endingHeadlines?.[id] ?? id, n]))} />
                <Bars title="Labels" counts={s.labels} />
              </li>
            ))}
          </ul>

          <h2 className="results__fb-title">Feedback</h2>
          {summary.feedback.length === 0 ? <p className="muted">No feedback yet.</p> : (
            <ul className="results__feedback">
              {summary.feedback.map((f, i) => (
                <li key={`${f.runId}-${i}`} className="scenario-card">
                  <p className="muted small">Run {f.runId}{f.mode === "quick" ? " · " : ""}{f.mode === "quick" && <span className="mode-tag">Quick play</span>}</p>
                  <dl>
                    {Object.entries(f.answers).filter(([, v]) => feedbackText(v).trim() !== "").map(([k, v]) => (
                      <div key={k} className="results__answer">
                        <dt>{FEEDBACK_QUESTIONS[k] ?? k}</dt>
                        <dd>{feedbackText(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </li>
              ))}
            </ul>
          )}

          <section className="contacts" aria-labelledby="contacts-title">
            <h2 id="contacts-title" className="results__fb-title">Contacts</h2>
            <p className="muted">
              Personal details from sign-up. They are not in the anonymised export: keep this list and the CSV confidential.
            </p>
            <div className="row-actions">
              <button type="button" className="btn" disabled={csvBusy || !contacts?.length} onClick={() => void downloadContacts()}>
                {csvBusy ? "Preparing…" : "Download contacts (CSV)"}
              </button>
            </div>
            {!contacts || contacts.length === 0 ? <p className="muted">No sign-ups yet.</p> : (
              <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="contacts-caption">
                <table className="acc-table contacts__table">
                  <caption id="contacts-caption" className="visually-hidden">Sign-ups, newest last</caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th><th scope="col">Company</th><th scope="col">Size</th><th scope="col">Email</th>
                      <th scope="col">Phone</th><th scope="col">Cohort</th><th scope="col">Signed up</th>
                      <th scope="col">Runs started</th><th scope="col">Runs ended</th><th scope="col">Results by email</th>
                      <th scope="col">Happy to be contacted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id}>
                        <th scope="row">{c.name}</th>
                        <td>{c.company ?? "–"}</td>
                        <td>{c.companySize ?? "–"}</td>
                        <td>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : "–"}</td>
                        <td>{c.phone ?? "–"}</td>
                        <td>{c.cohort ?? "–"}</td>
                        <td>{signedUpOn(c.signedUpAt)}</td>
                        <td className="num">{c.runsStarted}</td>
                        <td className="num">{c.runsEnded}</td>
                        <td>{contactEmailStatus(c)}</td>
                        <td>{c.contactConsent ? "Yes" : "No"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}
